<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4">
          <div
            class="card-header pb-0 d-flex justify-content-between align-items-center"
          >
            <div class="d-flex align-items-center gap-3">
              <button
                class="btn btn-outline-secondary btn-sm mb-0"
                @click="$router.push('/orders')"
              >
                <i class="fas fa-arrow-left me-1"></i> 返回
              </button>
              <button
                class="btn btn-primary btn-sm mb-0"
                @click="showChatModal = true"
              >
                <i class="fas fa-comments me-1"></i> 聯絡買家
              </button>
              <h6 class="mb-0">訂單詳情 #{{ order.order_number }}</h6>
            </div>
            <div>
              <soft-badge
                :color="getStatusColor(order.status)"
                variant="gradient"
                size="sm"
                class="me-2"
              >
                {{ formatStatus(order.status) }}
              </soft-badge>
              <soft-badge
                v-if="order.sub_status"
                color="secondary"
                variant="outline"
                size="sm"
              >
                {{ formatSubStatus(order.sub_status) }}
              </soft-badge>
            </div>
          </div>
          <div class="card-body">
            <!-- Info Cards -->
            <div class="row mb-4">
              <!-- Order Info -->
              <div class="col-md-4 mb-3">
                <div class="card h-100 border shadow-none">
                  <div class="card-body p-3">
                    <h6
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 mb-3"
                    >
                      訂單資訊
                    </h6>
                    <p class="text-sm mb-1">
                      <strong>日期:</strong> {{ formatDate(order.created_at) }}
                    </p>
                    <p class="text-sm mb-1">
                      <strong>付款方式:</strong>
                      {{ formatPaymentMethod(order.payment_method) }}
                    </p>
                    <p class="text-sm mb-1" v-if="order.payment_transaction_id">
                      <strong>交易編號:</strong>
                      {{ order.payment_transaction_id }}
                    </p>
                    <p class="text-sm mb-1">
                      <strong>付款狀態:</strong>
                      <span
                        :class="
                          order.sub_status === 'payment_pending_confirmation'
                            ? 'text-primary'
                            : order.payment_status === 'paid'
                            ? 'text-success'
                            : 'text-warning'
                        "
                        >{{
                          order.sub_status === "payment_pending_confirmation"
                            ? "已上傳憑證 (待確認)"
                            : formatPaymentStatus(order.payment_status)
                        }}</span
                      >
                    </p>
                    <p class="text-sm mb-1" v-if="order.paid_at">
                      <strong>付款時間:</strong> {{ formatDate(order.paid_at) }}
                    </p>
                    <p class="text-sm mb-1" v-if="order.payment_note">
                      <strong>付款備註:</strong>
                      <span class="text-danger">{{ order.payment_note }}</span>
                    </p>
                    <p class="text-sm mb-1" v-if="order.payment_proof_note">
                      <strong>付款憑證備註:</strong>
                      <span class="text-info">{{
                        order.payment_proof_note
                      }}</span>
                    </p>
                    <div
                      v-if="order.invoice_type === 'company'"
                      class="mt-3 pt-3 border-top"
                    >
                      <h6
                        class="text-xs font-weight-bold text-secondary text-uppercase mb-2"
                      >
                        發票資訊
                      </h6>
                      <p class="text-sm mb-1">
                        <strong>公司抬頭:</strong> {{ order.invoice_title }}
                      </p>
                      <p class="text-sm mb-1">
                        <strong>統一編號:</strong> {{ order.invoice_tax_id }}
                      </p>
                    </div>
                    <div v-if="order.payment_proof_image" class="mt-3">
                      <button
                        class="btn btn-sm btn-outline-primary mb-0 w-100"
                        @click="viewPaymentProof"
                      >
                        <i class="fas fa-image me-1"></i> 查看付款憑證
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Buyer Info -->
              <div class="col-md-4 mb-3">
                <div class="card h-100 border shadow-none">
                  <div class="card-body p-3">
                    <div
                      class="d-flex justify-content-between align-items-center mb-3"
                    >
                      <h6
                        class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 mb-0"
                      >
                        買家資訊
                      </h6>
                      <button
                        class="btn btn-link btn-sm p-0 m-0 text-secondary"
                        @click="openActionModal('edit_shipping')"
                        v-if="can('orders.edit')"
                      >
                        <i class="fas fa-edit"></i> 編輯
                      </button>
                    </div>
                    <p class="text-sm mb-1">
                      <strong>姓名:</strong> {{ displayBuyerName }}
                    </p>
                    <p class="text-sm mb-1">
                      <strong>電話:</strong> {{ displayBuyerPhone }}
                    </p>
                    <p class="text-sm mb-1">
                      <strong>地址:</strong> {{ order.shipping_city }},
                      {{ order.shipping_district }},
                      {{ order.shipping_address }}
                    </p>
                    <button
                      class="btn btn-link btn-sm px-0 text-primary"
                      @click="showBuyerDetails"
                    >
                      查看完整詳情
                    </button>
                  </div>
                </div>
              </div>
              <!-- Seller Info -->
              <div class="col-md-4 mb-3">
                <div class="card h-100 border shadow-none">
                  <div class="card-body p-3">
                    <h6
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 mb-3"
                    >
                      賣家資訊
                    </h6>
                    <p class="text-sm mb-1">
                      <strong>姓名:</strong>
                      {{ order.seller ? order.seller.name : "未知" }}
                    </p>
                    <p class="text-sm mb-1">
                      <strong>電子郵件:</strong>
                      {{ order.seller ? order.seller.email : "未知" }}
                    </p>
                    <button
                      class="btn btn-link btn-sm px-0 text-primary"
                      @click="showSellerDetails"
                    >
                      查看完整詳情
                    </button>
                  </div>
                </div>
              </div>

              <!-- Logistics Info (Pickup Store) -->
              <div
                class="col-md-4 mb-3"
                v-if="
                  order.pickup_store_name ||
                  (order.shipping_method &&
                    order.shipping_method.includes('7-11'))
                "
              >
                <div class="card h-100 border shadow-none">
                  <div class="card-body p-3">
                    <h6
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 mb-3"
                    >
                      物流資訊 (門市)
                    </h6>
                    <div class="d-flex flex-column">
                      <p class="text-sm mb-1">
                        <strong>門市:</strong> {{ order.pickup_store_name }}
                      </p>
                      <p class="text-sm mb-1">
                        <strong>地址:</strong> {{ order.pickup_store_address }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Refund Management -->
            <div
              class="row mb-4"
              v-if="order.refund_status || order.payment_status === 'paid'"
            >
              <div class="col-12">
                <div
                  class="card border border-danger shadow-none bg-danger-subtle"
                >
                  <div class="card-body p-3">
                    <div
                      class="d-flex justify-content-between align-items-center mb-2"
                    >
                      <div>
                        <h6
                          class="text-uppercase text-danger text-xxs font-weight-bolder opacity-7 mb-2"
                        >
                          退款管理
                        </h6>
                        <div class="d-flex align-items-center gap-2">
                          <span class="text-sm font-weight-bold"
                            >目前狀態:</span
                          >
                          <span class="badge bg-gradient-danger">{{
                            formatRefundStatus(order.refund_status || "無")
                          }}</span>
                        </div>
                      </div>
                      <div class="d-flex gap-2">
                        <a
                          v-if="paymentGatewayLink"
                          :href="paymentGatewayLink"
                          target="_blank"
                          class="btn btn-outline-danger btn-sm mb-0"
                        >
                          <i class="fas fa-external-link-alt me-1"></i> 前往
                          {{ paymentGatewayName }} 後台
                        </a>
                        <button
                          class="btn btn-danger btn-sm mb-0"
                          @click="openActionModal('refund_status')"
                        >
                          更新退款進度
                        </button>
                      </div>
                    </div>

                    <!-- Refund Details -->
                    <div v-if="order.latest_refund">
                      <hr class="horizontal dark my-2" />

                      <div class="mb-2" v-if="order.latest_refund.reason">
                        <span
                          class="text-xs text-secondary font-weight-bold uppercase"
                          >退貨原因:</span
                        >
                        <span class="text-sm ms-2">{{
                          order.latest_refund.reason
                        }}</span>
                      </div>

                      <div class="mb-2" v-if="order.latest_refund.refund_code">
                        <span
                          class="text-xs text-secondary font-weight-bold uppercase"
                          >退貨便代碼:</span
                        >
                        <span
                          class="text-lg ms-2 font-weight-bolder text-danger"
                          >{{ order.latest_refund.refund_code }}</span
                        >
                        <div class="text-xs text-secondary mt-1">
                          <i class="fas fa-info-circle me-1"></i>
                          {{ order.latest_refund.return_instruction }}
                        </div>
                      </div>

                      <div
                        v-if="
                          order.latest_refund.items &&
                          order.latest_refund.items.length > 0
                        "
                      >
                        <span
                          class="text-xs text-secondary font-weight-bold uppercase mb-1 d-block"
                          >退貨商品:</span
                        >
                        <ul class="list-group">
                          <li
                            v-for="item in order.latest_refund.items"
                            :key="item.id"
                            class="list-group-item border-0 p-1 ps-0 text-sm"
                          >
                            - {{ item.product_name }} x {{ item.quantity }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="row mb-4"
              v-if="
                order.status !== 'pending' &&
                (order.shipped_at || order.platform_shipped_at)
              "
            >
              <div class="col-12">
                <div class="card border shadow-none">
                  <div class="card-body p-3">
                    <h6
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 mb-3"
                    >
                      物流資訊
                    </h6>
                    <div class="row">
                      <div class="col-md-6" v-if="order.shipped_at">
                        <p class="text-sm mb-1">
                          <strong>賣家已出貨:</strong>
                          {{ formatDate(order.shipped_at) }}
                        </p>
                        <p class="text-sm mb-1">
                          <strong>追蹤號碼:</strong>
                          {{ order.tracking_number || "N/A" }}
                        </p>
                        <p class="text-sm mb-1" v-if="order.logistics_odno">
                          <strong>出貨單號 (ODNO):</strong>
                          {{ order.logistics_odno }}
                        </p>
                        <p
                          class="text-sm mb-1"
                          v-if="order.logistics_validation_no"
                        >
                          <strong>驗證碼:</strong>
                          {{ order.logistics_validation_no }}
                        </p>
                      </div>
                      <div class="col-md-6" v-if="order.platform_shipped_at">
                        <p class="text-sm mb-1">
                          <strong>平台已出貨:</strong>
                          {{ formatDate(order.platform_shipped_at) }}
                        </p>
                        <p
                          class="text-sm mb-1"
                          v-if="order.platform_logistics_provider"
                        >
                          <strong>物流商:</strong>
                          {{ order.platform_logistics_provider }}
                        </p>
                        <p class="text-sm mb-1">
                          <strong>追蹤號碼:</strong>
                          {{ order.platform_tracking_number || "N/A" }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <hr class="horizontal dark my-4" />

            <!-- Order Items -->
            <h6
              class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 mb-3"
            >
              訂單項目
            </h6>
            <div class="table-responsive p-0 mb-4">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                      width="50"
                    >
                      <input
                        type="checkbox"
                        @change="toggleAll"
                        :checked="allSelected"
                        :disabled="allConfirmed"
                      />
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      商品
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      目前庫存
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      訂單量
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      剩餘庫存 (可售)
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      價格
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      總計
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      認證
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in order.items" :key="item.id">
                    <td class="align-middle text-center">
                      <input
                        type="checkbox"
                        :value="item.id"
                        v-model="selectedItems"
                        :disabled="
                          item.is_stock_confirmed == 1 ||
                          item.is_stock_confirmed === true
                        "
                      />
                    </td>
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div>
                          <img
                            :src="
                              item.product.image_url ||
                              'https://placehold.co/100'
                            "
                            class="avatar avatar-sm me-3"
                            alt="product"
                          />
                        </div>
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ item.product.name }}</h6>
                          <p class="text-xs text-secondary mb-0">
                            {{
                              item.product.brand ? item.product.brand.name : ""
                            }}
                          </p>
                          <p
                            v-if="item.variant"
                            class="text-xs text-muted mb-0"
                          >
                            規格: {{ item.variant.display_name }} <br />
                            SKU: {{ item.variant.variant_code }}
                          </p>
                        </div>
                      </div>
                    </td>
                    <!-- Current Stock -->
                    <td class="align-middle text-center text-sm">
                      <span class="text-secondary text-xs font-weight-bold">
                        {{
                          item.variant ? item.variant.stock : item.product.stock
                        }}
                      </span>
                    </td>
                    <!-- Order Quantity -->
                    <td class="align-middle text-center text-sm">
                      <span class="text-dark text-xs font-weight-bold">{{
                        item.quantity
                      }}</span>
                    </td>
                    <!-- Remaining Stock (Available) -->
                    <!-- Calculation: Stock - Reserved -->
                    <td class="align-middle text-center text-sm">
                      <span class="text-secondary text-xs font-weight-bold">
                        {{
                          (item.variant
                            ? item.variant.stock
                            : item.product.stock) -
                          (item.variant
                            ? item.variant.reserved_stock
                            : item.product.reserved_stock)
                        }}
                      </span>
                    </td>
                    <!-- Price -->
                    <td class="align-middle text-center text-sm">
                      <span class="text-secondary text-xs font-weight-bold"
                        >NT$ {{ Math.ceil(item.price) }}</span
                      >
                    </td>
                    <!-- Total -->
                    <td class="align-middle text-center text-sm">
                      <span class="text-secondary text-xs font-weight-bold"
                        >NT$ {{ Math.ceil(item.price * item.quantity) }}</span
                      >
                    </td>
                    <!-- Certification -->
                    <td class="align-middle text-center text-sm">
                      <span
                        v-if="item.needs_certification"
                        class="badge badge-sm bg-gradient-info"
                        >必須</span
                      >
                      <span v-else class="text-secondary text-xs">無需</span>
                    </td>
                    <!-- Stock Status -->
                    <td class="align-middle text-center text-sm">
                      <span
                        v-if="item.is_stock_confirmed"
                        class="badge badge-sm bg-gradient-success"
                        >已扣庫</span
                      >
                      <span v-else class="badge badge-sm bg-gradient-secondary"
                        >未扣庫</span
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Financials -->
            <div class="row mt-4 justify-content-end">
              <div class="col-md-4">
                <div class="card bg-gray-100">
                  <div class="card-body">
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-sm">小計:</span>
                      <span class="text-sm font-weight-bold"
                        >NT$ {{ Math.ceil(order.subtotal) }}</span
                      >
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-sm">運費:</span>
                      <span class="text-sm font-weight-bold"
                        >NT$ {{ Math.ceil(order.shipping_fee) }}</span
                      >
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-sm">認證費:</span>
                      <span class="text-sm font-weight-bold"
                        >NT$ {{ Math.ceil(order.certification_fee) }}</span
                      >
                    </div>
                    <!-- Adjustment -->
                    <div
                      class="d-flex justify-content-between mb-2"
                      v-if="
                        order.adjustment_amount && order.adjustment_amount != 0
                      "
                    >
                      <span class="text-sm text-danger"
                        >調整金額 ({{ order.adjustment_reason }}):</span
                      >
                      <span class="text-sm font-weight-bold text-danger"
                        >NT$ {{ Math.ceil(order.adjustment_amount) }}</span
                      >
                    </div>
                    <hr class="horizontal dark my-2" />
                    <div class="d-flex justify-content-between">
                      <span class="text-lg font-weight-bold">總計:</span>
                      <span class="text-lg font-weight-bold text-primary"
                        >NT$ {{ Math.ceil(order.total_amount) }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Admin Actions -->
            <div class="row mt-4" v-if="can('orders.edit')">
              <div class="col-12">
                <div class="card bg-gradient-light border">
                  <div class="card-body">
                    <h6
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 mb-3"
                    >
                      管理員操作
                    </h6>
                    <div class="d-flex gap-2 flex-wrap">
                      <!-- Confirm Payment -->
                      <button
                        v-if="canConfirmPayment"
                        class="btn btn-success btn-sm"
                        @click="confirmPayment"
                      >
                        確認付款
                      </button>

                      <!-- Reject Payment -->
                      <button
                        v-if="canRejectPayment"
                        class="btn btn-danger btn-sm"
                        @click="rejectPayment"
                      >
                        退回付款憑證
                      </button>

                      <!-- Confirm Receipt -->
                      <button
                        v-if="canConfirmReceipt"
                        class="btn btn-success btn-sm"
                        @click="confirmReceipt"
                      >
                        確認收貨
                      </button>

                      <!-- Report Missing -->
                      <button
                        v-if="canReportMissing"
                        class="btn btn-warning btn-sm"
                        @click="openActionModal('missing')"
                      >
                        回報缺件
                      </button>

                      <!-- Start Processing (No Cert) -->
                      <button
                        v-if="canStartProcessing"
                        class="btn btn-info btn-sm"
                        @click="openActionModal('processing')"
                      >
                        開始處理
                      </button>

                      <!-- Start Verification (With Cert) -->
                      <button
                        v-if="canStartVerification"
                        class="btn btn-info btn-sm"
                        @click="openActionModal('verification')"
                      >
                        開始驗證
                      </button>

                      <!-- Verification Result -->
                      <button
                        v-if="isVerifying"
                        class="btn btn-success btn-sm"
                        @click="updateVerification('success')"
                      >
                        驗證成功
                      </button>
                      <button
                        v-if="isVerifying"
                        class="btn btn-danger btn-sm"
                        @click="updateVerification('failed')"
                      >
                        驗證失敗
                      </button>

                      <!-- Confirm Stock (New) -->
                      <button
                        v-if="canConfirmStock"
                        class="btn btn-warning btn-sm"
                        @click="confirmStock"
                      >
                        確認庫存 (扣除)
                      </button>

                      <!-- Edit Status (New) -->
                      <button
                        v-if="canEditStatus"
                        class="btn btn-outline-secondary btn-sm"
                        @click="openActionModal('status')"
                      >
                        修改狀態
                      </button>

                      <!-- Platform Ship -->
                      <button
                        v-if="canPlatformShip"
                        class="btn btn-primary btn-sm"
                        @click="openActionModal('ship')"
                      >
                        {{
                          order.status === "shipped"
                            ? "編輯物流資訊"
                            : "出貨給買家"
                        }}
                      </button>

                      <!-- Print Label Button -->
                      <button
                        v-if="order.tracking_number"
                        class="btn btn-outline-info btn-sm"
                        @click="printLabel"
                      >
                        列印託運單
                      </button>

                      <!-- Cancel Actions -->
                      <button
                        v-if="canCancel"
                        class="btn btn-outline-danger btn-sm"
                        @click="cancelOrder"
                      >
                        取消訂單
                      </button>

                      <button
                        v-if="canReturnCancel"
                        class="btn btn-outline-danger btn-sm"
                        @click="openActionModal('return_cancel')"
                      >
                        退貨並取消
                      </button>

                      <!-- QR Code & Outbound -->
                      <button
                        class="btn btn-outline-dark btn-sm"
                        @click="showQrCode"
                      >
                        顯示 QR Code
                      </button>

                      <!-- Price Adjustment -->
                      <button
                        class="btn btn-secondary btn-sm"
                        @click="openActionModal('adjust_price')"
                      >
                        調整金額 / 折扣
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- QR Code Modal -->
    <div
      v-if="showQrModal"
      class="modal fade show d-block"
      style="background: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">訂單 QR Code</h5>
            <button
              type="button"
              class="btn btn-link text-dark p-0 mb-0"
              @click="closeQrModal"
            >
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>
          <div class="modal-body text-center">
            <p class="mb-3">
              掃描此代碼以管理訂單出庫:
              <strong>{{ order.order_number }}</strong>
            </p>
            <div class="d-flex justify-content-center">
              <img
                :src="`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=O:${order.id}`"
                alt="QR Code"
              />
            </div>
            <p class="mt-3 text-muted text-xs">ID: {{ order.id }}</p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeQrModal"
            >
              關閉
            </button>
            <button type="button" class="btn btn-primary" @click="printQr">
              列印
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Modal -->
    <div
      v-if="showModal"
      class="modal fade show d-block"
      style="background: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ modalTitle }}</h5>
            <button
              type="button"
              class="btn btn-link text-dark p-0 mb-0"
              @click="closeModal"
            >
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>
          <div class="modal-body">
            <div
              v-if="
                modalType === 'missing' ||
                modalType === 'processing' ||
                modalType === 'verification'
              "
            >
              <label>開箱影片 (必填)</label>
              <input
                type="file"
                class="form-control mb-3"
                @change="handleFileUpload"
              />
            </div>

            <div v-if="modalType === 'ship' || modalType === 'return_cancel'">
              <label>物流商</label>
              <!-- Dropdown for Provider Selection -->
              <select
                class="form-control mb-3"
                v-model="modalData.logistics_provider"
              >
                <option value="">請選擇物流商</option>
                <option value="payuni_711">7-11 C2C (PayUni 自動開單)</option>
                <option value="black_cat">黑貓宅急便</option>
                <option value="post_office">中華郵政</option>
                <option value="hct">新竹物流</option>
                <option value="kerry">嘉里大榮</option>
                <option value="other">其他</option>
              </select>

              <!-- Show tracking number input unless PayUni is selected (it auto-generates) -->
              <div v-if="modalData.logistics_provider !== 'payuni_711'">
                <label>追蹤號碼</label>
                <input
                  type="text"
                  class="form-control mb-3"
                  v-model="modalData.tracking_number"
                  placeholder="請輸入單號"
                />
              </div>
              <div v-else>
                <div class="alert alert-info text-xs mb-3">
                  <strong>提示：</strong> 系統將自動向 PayUni 申請 7-11
                  物流單號，請確保訂單已有門市資訊。
                </div>
              </div>
            </div>

            <div v-if="modalType === 'status'">
              <label>訂單狀態</label>
              <select class="form-control mb-3" v-model="modalData.status">
                <option value="pending">待付款 (Pending)</option>
                <option value="paid">已付款 (Paid)</option>
                <option value="processing">處理中/待出貨 (Processing)</option>
                <option value="completed">已完成 (Completed)</option>
                <option value="cancelled">已取消 (Cancelled)</option>
              </select>
            </div>

            <div v-if="modalType === 'adjust_price'">
              <label>調整金額 (負數為折扣)</label>
              <input
                type="number"
                class="form-control mb-3"
                v-model="modalData.adjustment_amount"
              />
              <label>調整原因</label>
              <input
                type="text"
                class="form-control mb-3"
                v-model="modalData.adjustment_reason"
              />
            </div>

            <div v-if="modalType === 'edit_shipping'">
              <h6 class="text-sm font-weight-bold mb-2">收件人資訊</h6>
              <div class="form-group">
                <label class="text-xs">姓名</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="modalData.recipient_name"
                />
              </div>
              <div class="form-group">
                <label class="text-xs">電話</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="modalData.recipient_phone"
                />
              </div>

              <hr class="horizontal dark my-3" />

              <div v-if="order.pickup_store_name">
                <h6 class="text-sm font-weight-bold mb-2">超商取貨資訊</h6>
                <div class="form-group">
                  <label class="text-xs">門市名稱</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="modalData.pickup_store_name"
                  />
                </div>
                <div class="form-group">
                  <label class="text-xs">門市代號 (Store ID)</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="modalData.pickup_store_id"
                  />
                </div>
                <div class="form-group">
                  <label class="text-xs">門市地址</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="modalData.pickup_store_address"
                  />
                </div>
              </div>
              <div v-else>
                <h6 class="text-sm font-weight-bold mb-2">宅配地址資訊</h6>
                <div class="form-group">
                  <label class="text-xs">城市</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="modalData.shipping_city"
                  />
                </div>
                <div class="form-group">
                  <label class="text-xs">區域</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="modalData.shipping_district"
                  />
                </div>
                <div class="form-group">
                  <label class="text-xs">郵遞區號</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="modalData.shipping_postal_code"
                  />
                </div>
                <div class="form-group">
                  <label class="text-xs">詳細地址</label>
                  <textarea
                    class="form-control"
                    rows="2"
                    v-model="modalData.shipping_address"
                  ></textarea>
                </div>
              </div>
            </div>

            <div v-if="modalType === 'refund_status'">
              <label>退款進度 (前端用戶可見)</label>
              <select
                class="form-control mb-3"
                v-model="modalData.refund_status"
              >
                <option value="applying">申請中 (Applying)</option>
                <option value="accepted">已受理 (Accepted)</option>
                <option value="waiting_return">待退貨 (Waiting Return)</option>
                <option value="checking">確認中/檢查商品 (Checking)</option>
                <option value="refunded">已退款 (Refunded)</option>
                <option value="cancelled">
                  取消退款申請 (Cancel Application)
                </option>
              </select>
              <div class="alert alert-warning text-xs">
                <strong>提示:</strong> 更新為「已退款」將標記訂單為退款完成。
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              關閉
            </button>
            <button type="button" class="btn btn-primary" @click="submitAction">
              確認
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Chat Modal -->
    <div
      v-if="showChatModal"
      class="modal fade show d-block"
      style="background: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content" style="height: 80vh">
          <div class="modal-header">
            <h5 class="modal-title">
              與 {{ order.buyer?.name || order.recipient_name }} 的對話
            </h5>
            <button
              type="button"
              class="btn btn-link text-dark p-0 mb-0"
              @click="showChatModal = false"
            >
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>
          <div class="modal-body p-0 h-100 overflow-hidden">
            <order-chat
              :orderId="order.order_number"
              :order="order"
              class="h-100 border-0 shadow-none rounded-0"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SoftBadge from "@/components/SoftBadge.vue";

import OrderChat from "@/components/OrderChat.vue";
import { hasPermission } from "@/utils/permissions";
import request from "@/utils/request";
import Swal from "sweetalert2";

export default {
  name: "OrderDetail",
  components: {
    SoftBadge,

    OrderChat,
  },
  data() {
    return {
      order: {
        items: [],
        seller: {},
        buyer: {},
      },
      showModal: false,
      modalType: "",
      modalData: {
        logistics_provider: "",
        tracking_number: "",
        file: null,
      },
      showQrModal: false,

      showChatModal: false,
      currentUser: JSON.parse(localStorage.getItem("user") || "{}"),
      selectedItems: [],
    };
  },
  computed: {
    allSelected() {
      const available = this.order.items.filter((i) => !i.is_stock_confirmed);
      return (
        available.length > 0 && this.selectedItems.length === available.length
      );
    },
    allConfirmed() {
      return this.order.items.every((i) => i.is_stock_confirmed);
    },
    modalTitle() {
      switch (this.modalType) {
        case "missing":
          return "回報缺件";
        case "processing":
          return "開始處理";
        case "verification":
          return "開始驗證";
        case "ship":
          return "出貨給買家";
        case "return_cancel":
          return "退貨並取消";
        case "adjust_price":
          return "調整訂單金額";
        case "status":
          return "修改訂單狀態";
        default:
          return "操作";
      }
    },
    paymentGatewayLink() {
      if (this.order.payment_method === "line_pay") {
        return "https://pay.line.me/epi/center/deal/integrate?locale=zh_TW";
      }
      if (
        this.order.payment_method === "ctbc_credit_card" ||
        this.order.payment_method === "credit_card"
      ) {
        return "https://epos.ctbcbank.com/login.jsp#";
      }
      return null;
    },
    paymentGatewayName() {
      if (this.order.payment_method === "line_pay") return "LINE Pay";
      if (
        this.order.payment_method === "ctbc_credit_card" ||
        this.order.payment_method === "credit_card"
      )
        return "中信後台";
      return "金流後台";
    },
    canConfirmPayment() {
      // Show if payment is pending and (has note OR has receipt) OR just pending
      return (
        this.order.status !== "cancelled" &&
        this.order.status !== "cancelled" &&
        this.order.payment_status === "pending" &&
        this.order.sub_status !== "payment_declined"
      );
    },
    // Add logic to check if we can reject
    canRejectPayment() {
      return (
        this.order.status !== "cancelled" &&
        this.order.payment_status === "pending" &&
        this.order.payment_proof_image &&
        this.order.sub_status !== "payment_declined"
      );
    },
    canConfirmReceipt() {
      return (
        this.order.status === "shipped" &&
        (!this.order.sub_status || this.order.sub_status === "seller_shipped")
      );
    },
    canReportMissing() {
      return this.order.sub_status === "product_pending_confirmation";
    },
    canStartProcessing() {
      return (
        this.order.sub_status === "product_pending_confirmation" &&
        !this.hasCertificationItems
      );
    },
    canStartVerification() {
      return (
        this.order.sub_status === "product_pending_confirmation" &&
        this.hasCertificationItems
      );
    },
    isVerifying() {
      return this.order.sub_status === "platform_verifying";
    },
    canPlatformShip() {
      // Allow shipping if processing or already shipped (to update logistics)
      // Must be stock confirmed first
      return (
        this.order.is_stock_confirmed &&
        (this.order.status === "processing" || this.order.status === "shipped")
      );
    },
    canConfirmStock() {
      // Allow confirming stock if there are items not yet confirmed
      return (
        this.order.items.some((i) => !i.is_stock_confirmed) &&
        this.order.status !== "cancelled" &&
        this.order.status !== "completed"
      );
    },
    canEditStatus() {
      return this.order.status !== "shipped";
    },
    canCancel() {
      return (
        this.order.status !== "shipped" &&
        this.order.status !== "completed" &&
        this.order.status !== "cancelled"
      );
    },
    canReturnCancel() {
      return (
        this.order.sub_status === "verification_failed" ||
        this.order.sub_status === "missing_items_overdue" ||
        this.order.status === "shipped"
      );
    },
    displayBuyerName() {
      return (
        this.order.shipping_name ||
        this.order.recipient_name ||
        (this.order.buyer_first_name
          ? `${this.order.buyer_first_name} ${this.order.buyer_last_name || ""}`
          : "") ||
        this.order.buyer?.name ||
        "N/A"
      );
    },
    displayBuyerPhone() {
      return (
        this.order.shipping_phone ||
        this.order.recipient_phone ||
        this.order.buyer_phone ||
        this.order.buyer?.phone ||
        "N/A"
      );
    },
    hasCertificationItems() {
      return this.order.items.some((item) => item.needs_certification);
    },
  },
  methods: {
    viewPaymentProof() {
      if (this.order.payment_proof_image) {
        Swal.fire({
          title: "付款憑證",
          imageUrl: this.order.payment_proof_image,
          imageAlt: "Payment Proof",
          confirmButtonText: "關閉",
          width: 600,
        });
      }
    },
    can(permission) {
      return hasPermission(this.currentUser, permission);
    },
    getStatusColor(status) {
      switch (status) {
        case "completed":
          return "success";
        case "pending":
          return "warning";
        case "processing":
          return "info";
        case "shipped":
          return "primary";
        case "cancelled":
          return "danger";
        default:
          return "secondary";
      }
    },
    formatStatus(status) {
      const statusMap = {
        pending: "待處理",
        pending_payment: "待付款",
        confirmed: "已確認",
        processing: "處理中",
        shipped: "已出貨",
        delivered: "已送達",
        completed: "已完成",
        cancelled: "已取消",
        refunded: "已退款",
      };
      return statusMap[status] || status;
    },
    formatSubStatus(status) {
      if (!status) return "";
      const subStatusMap = {
        product_pending_confirmation: "商品待確認",
        platform_pending_shipment: "平台待出貨",
        seller_pending_shipment: "賣家待出貨",
        pending_receipt: "待收貨",
        pending_review: "待評價",
        platform_verifying: "平台驗證中",
        verification_failed: "驗證失敗",
        missing_items_overdue: "缺件逾期",
        seller_shipped: "賣家已出貨",
        payment_pending_confirmation: "付款待確認",
        payment_declined: "付款被退回",
        picked_up: "已取貨",
        platform_shipped: "平台已出貨",
        PLATFORM_SHIPPED: "平台已出貨",
        // PayUni Logistics Statuses
        logistics_processing: "物流處理中",
        in_transit: "運送中",
        arrived_at_store: "已送達門市",
        delivery_abnormal: "配送異常",
        cancelled_by_logistics: "物流取消",
        logistics_lost: "包裹遺失",
        buyer_not_picked_up: "買家逾期未取",
        returned_to_seller: "已退回賣家",
        returned_to_store_seller_not_picked_up: "退回門市 (賣家未取)",
        returning_via_delivery: "退回賣家中 (宅配)",
        store_closed_redirect_needed: "門市關轉 (需重選)",
      };
      return subStatusMap[status] || status;
    },
    formatPaymentStatus(status) {
      const paymentStatusMap = {
        pending: "待付款",
        paid: "已付款",
        failed: "付款失敗",
        refunded: "已退款",
      };
      return paymentStatusMap[status] || status;
    },
    formatRefundStatus(status) {
      const map = {
        applying: "申請中",
        accepted: "已受理",
        waiting_return: "待退貨",
        checking: "確認中",
        refunded: "已退款",
        cancelled: "已取消",
        無: "無",
      };
      return map[status] || status;
    },
    formatPaymentMethod(method) {
      const methodMap = {
        atm: "ATM轉帳",
        bank_transfer: "銀行轉帳",
        credit_card: "信用卡",
        ctbc_credit_card: "信用卡 (中國信託)",
        third_party: "第三方支付",
        line_pay: "LINE Pay",
      };
      return methodMap[method] || method;
    },
    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleString();
    },
    async fetchOrder() {
      try {
        const response = await request.get(
          `/api/admin/orders/${this.$route.params.id}`
        );
        this.order = response.data.data;
      } catch (error) {
        console.error("Failed to fetch order", error);
      }
    },
    showSellerDetails() {
      const s = this.order.seller;
      if (s) {
        Swal.fire({
          title: "賣家資訊",
          html: `<p>姓名: ${s.name}</p><p>電子郵件: ${s.email}</p><p>電話: ${s.phone}</p><p>銀行: ${s.bank_name} (${s.bank_account})</p>`,
          icon: "info",
        });
      }
    },
    showBuyerDetails() {
      const b = this.order.buyer || {};
      const name = this.order.buyer_first_name
        ? `${this.order.buyer_first_name} ${this.order.buyer_last_name || ""}`
        : b.name || this.order.recipient_name;

      const email = this.order.buyer_email || b.email || "N/A";
      const phone =
        this.order.buyer_phone ||
        b.phone ||
        this.order.recipient_phone ||
        "N/A";

      Swal.fire({
        title: "買家資訊",
        html: `<p>姓名: ${name}</p><p>電子郵件: ${email}</p><p>電話: ${phone}</p>`,
        icon: "info",
      });
    },

    // Actions
    async confirmPayment() {
      this.$swal
        .fire({
          title: "確認將付款狀態設為已付款？",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "確認",
          cancelButtonText: "取消",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            try {
              await request.post(
                `/api/admin/orders/${this.order.id}/payment-status`,
                { payment_status: "paid" }
              );
              this.fetchOrder();
              this.$swal.fire("成功", "付款狀態已更新", "success");
            } catch (e) {
              this.$swal.fire("錯誤", "更新失敗", "error");
            }
          }
        });
    },
    async rejectPayment() {
      const { value: reason } = await Swal.fire({
        title: "退回付款憑證",
        input: "text",
        inputLabel: "退回原因",
        inputPlaceholder: "請輸入退回原因...",
        showCancelButton: true,
      });

      if (reason) {
        try {
          await request.post(
            `/api/admin/orders/${this.order.id}/payment-reject`,
            { reason }
          );
          this.fetchOrder();
          this.$swal.fire("成功", "付款憑證已退回", "success");
        } catch (e) {
          this.$swal.fire("錯誤", "操作失敗", "error");
        }
      }
    },
    async confirmReceipt() {
      this.$swal({
        title: "確認收到賣家的商品？",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "確認",
        cancelButtonText: "取消",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await request.post(
              `/api/admin/orders/${this.order.id}/confirm-receipt`
            );
            this.fetchOrder();
            this.$swal.fire("成功", "已確認收貨", "success");
          } catch (e) {
            this.$swal.fire("錯誤", "確認收貨失敗", "error");
          }
        }
      });
    },
    async printLabel() {
      try {
        const response = await request.get(
          `/api/admin/orders/${this.order.id}/print-label`
        );
        const { action, params } = response.data.data;

        // Create form
        const form = document.createElement("form");
        form.method = "POST";
        form.action = action;
        form.target = "_blank"; // Open in new tab

        for (const key in params) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = params[key];
          form.appendChild(input);
        }

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      } catch (e) {
        console.error(e);
        this.$swal.fire("錯誤", "無法取得列印資訊", "error");
      }
    },
    async updateVerification(result) {
      this.$swal({
        title: `將驗證標記為 ${result === "success" ? "成功" : "失敗"}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "確認",
        cancelButtonText: "取消",
      }).then(async (res) => {
        if (res.isConfirmed) {
          try {
            await request.post(
              `/api/admin/orders/${this.order.id}/verification-result`,
              { result }
            );
            this.fetchOrder();
            this.$swal.fire("成功", "驗證結果已更新", "success");
          } catch (e) {
            this.$swal.fire("錯誤", "更新驗證失敗", "error");
          }
        }
      });
    },
    async cancelOrder() {
      this.$swal({
        title: "您確定要取消此訂單嗎？",
        text: "此操作無法復原",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ea0606",
        confirmButtonText: "確定取消",
        cancelButtonText: "取消",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await request.post(`/api/admin/orders/${this.order.id}/cancel`);
            this.fetchOrder();
            this.$swal.fire("已取消", "訂單已成功取消", "success");
          } catch (e) {
            this.$swal.fire("錯誤", "取消訂單失敗", "error");
          }
        }
      });
    },

    // Modal Actions
    openActionModal(type) {
      this.modalType = type;
      this.modalData = {
        logistics_provider: "",
        tracking_number: "",
        file: null,
      };

      // Pre-fill for ship edit
      if (type === "ship") {
        if (this.order.platform_logistics_provider) {
          this.modalData.logistics_provider = this.order.platform_logistics_provider;
        }
        if (this.order.platform_tracking_number) {
          this.modalData.tracking_number = this.order.platform_tracking_number;
        } else if (this.order.tracking_number) {
          this.modalData.tracking_number = this.order.tracking_number;
        }
      }

      if (type === "edit_shipping") {
        this.modalTitle = "編輯收件資訊";
        this.modalData = {
          recipient_name: this.order.shipping_name || this.order.recipient_name, // Fallback just in case
          recipient_phone:
            this.order.shipping_phone || this.order.recipient_phone,
          shipping_address: this.order.shipping_address,
          shipping_city: this.order.shipping_city,
          shipping_district: this.order.shipping_district,
          shipping_postal_code: this.order.shipping_postal_code,
          pickup_store_name: this.order.pickup_store_name,
          pickup_store_id: this.order.pickup_store_name
            ? this.order.pickup_store_id || ""
            : "",
          pickup_store_address: this.order.pickup_store_address,
        };
      } else if (type === "return_cancel") {
        if (this.order.platform_logistics_provider) {
          this.modalData.logistics_provider = this.order.platform_logistics_provider;
        }
        if (this.order.platform_tracking_number) {
          this.modalData.tracking_number = this.order.platform_tracking_number;
        } else if (this.order.tracking_number) {
          this.modalData.tracking_number = this.order.tracking_number;
        }
      }

      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    handleFileUpload(event) {
      this.modalData.file = event.target.files[0];
    },
    async confirmStock() {
      this.$swal
        .fire({
          title: "確認扣除庫存？",
          text: "這將會從實際庫存中扣除數量，並標記為已確認。",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "確認",
          cancelButtonText: "取消",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            if (this.selectedItems.length === 0) {
              this.$swal.fire("提示", "請至少選擇一個商品", "warning");
              return;
            }
            try {
              await request.post(
                `/api/admin/orders/${this.order.id}/confirm-stock`,
                { item_ids: this.selectedItems }
              );
              this.fetchOrder();
              this.selectedItems = []; // clear selection
              this.$swal.fire("成功", "所選商品庫存已扣除", "success");
            } catch (e) {
              this.$swal.fire(
                "錯誤",
                e.response?.data?.message || "操作失敗",
                "error"
              );
            }
          }
        });
    },
    toggleAll(e) {
      if (e.target.checked) {
        this.selectedItems = this.order.items
          .filter((i) => !i.is_stock_confirmed)
          .map((i) => i.id);
      } else {
        this.selectedItems = [];
      }
    },
    async submitAction() {
      // Handle Status Update
      if (this.modalType === "status") {
        try {
          await request.post(`/api/admin/orders/${this.order.id}/status`, {
            status: this.modalData.status,
          });
          this.closeModal();
          this.fetchOrder();
          this.$swal.fire("成功", "狀態已更新", "success");
        } catch (e) {
          this.$swal.fire("錯誤", "狀態更新失敗", "error");
        }
        return;
      }

      const formData = new FormData();
      if (this.modalData.file) formData.append("video", this.modalData.file);
      if (this.modalData.logistics_provider)
        formData.append(
          "logistics_provider",
          this.modalData.logistics_provider
        );
      if (this.modalData.tracking_number)
        formData.append("tracking_number", this.modalData.tracking_number);

      if (this.modalType === "adjust_price") {
        if (this.modalData.adjustment_amount) {
          formData.append(
            "adjustment_amount",
            this.modalData.adjustment_amount
          );
        }
        if (this.modalData.adjustment_reason) {
          formData.append(
            "adjustment_reason",
            this.modalData.adjustment_reason
          );
        }
      }

      if (this.modalType === "refund_status") {
        formData.append("refund_status", this.modalData.refund_status);
      }

      let endpoint = "";
      switch (this.modalType) {
        case "missing":
          endpoint = "report-missing";
          break;
        case "processing":
          endpoint = "start-processing";
          break;
        case "verification":
          endpoint = "start-verification";
          break;
        case "ship":
          endpoint = "ship-platform";
          break;
        case "return_cancel":
          endpoint = "return-cancel";
          break;
        case "adjust_price":
          endpoint = "adjust-price";
          break;

        case "refund_status":
          endpoint = "refund-status"; // Routes definition: orders/{id}/refund-status
          break;
        case "edit_shipping":
          endpoint = "shipping-info";
          break;
      }

      try {
        if (this.modalType === "edit_shipping") {
          await request.put(`/api/admin/orders/${this.order.id}/${endpoint}`, {
            recipient_name: this.modalData.recipient_name,
            recipient_phone: this.modalData.recipient_phone,
            shipping_address: this.modalData.shipping_address,
            shipping_city: this.modalData.shipping_city,
            shipping_district: this.modalData.shipping_district,
            shipping_postal_code: this.modalData.shipping_postal_code,
            pickup_store_name: this.modalData.pickup_store_name,
            pickup_store_id: this.modalData.pickup_store_id,
            pickup_store_address: this.modalData.pickup_store_address,
          });
        } else {
          await request.post(
            `/api/admin/orders/${this.order.id}/${endpoint}`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
        }
        this.closeModal();
        this.fetchOrder();
        this.$swal.fire("成功", "操作成功", "success");
      } catch (e) {
        this.$swal.fire(
          "錯誤",
          e.response?.data?.message || "操作失敗",
          "error"
        );
      }
    },
    showQrCode() {
      this.showQrModal = true;
    },
    closeQrModal() {
      this.showQrModal = false;
    },
    printQr() {
      window.print();
    },
  },
  created() {
    this.fetchOrder();
  },
};
</script>
